/**
 * Related works and services of a service listing (spec
 * 2026-10-26-related-works-and-services-design.md). The freelancer picks up to
 * three of each in the editor; the IDs live in publicData.relatedWorkIds and
 * publicData.relatedServiceIds, in pick order.
 */

export const MAX_RELATED_ITEMS = 3;

/**
 * Picked listing IDs from publicData: anything but an array reads as no
 * picks; non-string, empty and repeated entries are dropped; at most three.
 */
export const readRelatedIds = value => {
  if (!Array.isArray(value)) {
    return [];
  }
  const ids = value.filter(id => typeof id === 'string' && id.length > 0);
  return [...new Set(ids)].slice(0, MAX_RELATED_ITEMS);
};

const publicDataOf = listing => listing?.attributes?.publicData || {};

// A published portfolio work visitors can see.
export const isPickableWork = listing => {
  const publicData = publicDataOf(listing);
  return publicData.listingType === 'portfolioWork' && !publicData.privateWork;
};

// A published service visitors can see, other than `excludeId` (the listing
// being edited or shown).
export const isPickableService = (listing, excludeId) => {
  const publicData = publicDataOf(listing);
  return (
    publicData.listingType === 'service' && !publicData.hidden && listing?.id?.uuid !== excludeId
  );
};

/**
 * What the service page shows from `pool` (newest first): the picked
 * listings in pick order, only those; or the newest three when none of the
 * picks is in the pool.
 */
export const pickRelated = (ids, pool) => {
  const picked = (ids || [])
    .map(id => pool.find(listing => listing.id.uuid === id))
    .filter(Boolean);
  return (picked.length > 0 ? picked : pool).slice(0, MAX_RELATED_ITEMS);
};

/**
 * The IDs to save. With the loaded `pool`, picks that are no longer in it are
 * dropped; with `pool` null (the list didn't load) the saved picks are kept.
 */
export const relatedIdsForSave = (ids, pool) => {
  const saved = readRelatedIds(ids);
  if (pool === null) {
    return saved;
  }
  const poolIds = new Set(pool.map(listing => listing.id.uuid));
  return saved.filter(id => poolIds.has(id));
};

// Picking appends, unpicking removes (the rest renumber); a fourth pick is refused.
export const togglePick = (selectedIds, id) => {
  if (selectedIds.includes(id)) {
    return selectedIds.filter(selected => selected !== id);
  }
  return selectedIds.length < MAX_RELATED_ITEMS ? [...selectedIds, id] : selectedIds;
};
